// Runtime configuration, fetched from /config.json rather than baked into the
// build: one build serves local dev (public/config.json) and every AWS stage (the
// CDK stack writes config.json next to the app on deploy).

export type AuthConfig =
  | { mode: 'dev' }
  | { mode: 'cognito'; signInUrl: string; clientId: string };

export interface AppConfig {
  /** '' when the API is on the same origin (the Vite dev proxy). */
  apiUrl: string;
  auth: AuthConfig;
  /** Features this stage hides (infra/stages.ts `hiddenFeatures`); absent: none. */
  hidden?: Feature[];
}

/** Built features a stage can keep out of sight until an agency is ready for them. */
export type Feature = 'add-to-brief' | 'agent-redaction' | 'appraisal-report' | 'calendar' | 'campaigns' | 'commission' | 'documents' | 'duplicates' | 'edit-conflicts' | 'email-settings' | 'enquiry-contact-guard' | 'enquiry-settings' | 'esign' | 'follow-ups' | 'import' | 'inspections' | 'land' | 'letters' | 'marketing' | 'offline' | 'open-home-notices' | 'performance' | 'portal-limits' | 'portal-rent' | 'portal-view' | 'privacy-erasure' | 'readvertise' | 'rental-views' | 'rentroll' | 'response-times' | 'settlement-contacts' | 'social' | 'social-connect' | 'task-from-email' | 'trust' | 'vendor-online';

let current: AppConfig | undefined;

export async function loadConfig(): Promise<AppConfig> {
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) throw new Error(`Could not load /config.json (${res.status})`);
  current = (await res.json()) as AppConfig;
  current.apiUrl = current.apiUrl.replace(/\/$/, '');
  return current;
}

export function config(): AppConfig {
  if (!current) throw new Error('config() called before loadConfig()');
  return current;
}

/** Whether this stage shows a feature (prod hides some that dev keeps). */
export function shown(feature: Feature): boolean {
  return !config().hidden?.includes(feature);
}
